import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, shareReplay, tap } from 'rxjs/operators';
import { LCID_TO_LANG } from '../Translate_Services/lcid-mapping';
import { BrandingSettingsResponse, BrandingStyles } from '../../Modules/Common/Models/branding-settings.model';

export interface TgSettings {
  DefLocaleId?: number;
  DateFormat?: number;
  [key: string]: any;
}

export interface JobFieldsToDisplay {
  Position1?: string;
  JobTitle?: string;
  Position3?: string[];
  Summary?: string;
}
export interface TGLinksInfo {
  Name?: string;
  Value?: string;
  onlyIcon?: string;
  Icon?: string;
  ContentType?: string;
  CustomHeaderLinksInfo?: any;
}

export interface TGLocaleInfo {
  SiteId: number;
  LocaleId: string;
  LocaleName: string;
  SiteLocaleName: string;
  DefLangId: string;
}

export interface TgSettingsResponse {
  tgSettings: TgSettings;
  headerInfo: TGLinksInfo[];
  footerInfo: TGLinksInfo[];
  TGLocales?: TGLocaleInfo[];
  searchresJobFieldsToDisplay?:JobFieldsToDisplay;
  BrandingConfiguration?: BrandingSettingsResponse;
}

@Injectable({
  providedIn: 'root'
})
export class TgSettingsService {
  private apiBase = '/TgNewUI/CandidateZone/Ajax';
  private settingsSubject = new BehaviorSubject<TgSettingsResponse | null>(null);
  public settings$ = this.settingsSubject.asObservable();
  private settingsLoad$!: Observable<TgSettingsResponse>;

  constructor(private http: HttpClient) {
    // Lazy initialization - only load when someone subscribes
    this.settingsLoad$ = this.createSettingsLoadObservable().pipe(
      shareReplay(1),
      catchError(() => of({ tgSettings: {}, headerInfo: [], footerInfo: [] }))
    );

    // Trigger the load
    this.settingsLoad$.subscribe();
  }
  getBrandingConfig(): BrandingStyles {
    const rawConfig = this.getCurrentSettings()?.BrandingConfiguration;
    
    // Default fallback values
    const defaultStyles = {
      text: '#333333', 
      font: "'Helvetica Neue', Helvetica, Arial, sans-serif",
      focus: '#008571', 
      headerBg: '#008571', 
      bg: '#ffffff', 
      fontSize: '16px',
      hoverBg: '#f9f9f9', 
      activeText: '#333333', 
      inputBg: '#ffffff',
      borderColor: '#333333', 
      borderRadius: '0px', 
      controlHeight: '32px',
      controlMaxWidth: '300px', 
      disabledColor: '#8f8f8f'
    };

    let parsedConfig: any = null;
    if (rawConfig) {
      if (typeof rawConfig === 'string') {
        try { parsedConfig = JSON.parse(rawConfig); } catch (e) { console.error(e); }
      } else {
        parsedConfig = rawConfig;
      }
    }

    const bs = parsedConfig?.brandingSettings || {};
    const styles = {} as BrandingStyles;

    // Direct mappings with null checks
    styles.text = bs.Responsive_BaseFontColor ? bs.Responsive_BaseFontColor : defaultStyles.text;
    styles.font = bs.Responsive_BaseFontFamily ? bs.Responsive_BaseFontFamily : defaultStyles.font;
    styles.focus = bs.Responsive_ButtonBackgroundColor ? bs.Responsive_ButtonBackgroundColor : defaultStyles.focus;
    styles.headerBg = bs.Responsive_HeaderBackgroundColor ? bs.Responsive_HeaderBackgroundColor : defaultStyles.headerBg;
    styles.bg = bs.Responsive_BaseBackgroundColor ? bs.Responsive_BaseBackgroundColor : defaultStyles.bg;
    styles.fontSize = bs.Responsive_BaseFontSize ? bs.Responsive_BaseFontSize : defaultStyles.fontSize;

    const css = bs.Responsive_CompiledCSS || bs.Responsive_AdvancedCSS || defaultStyles || '';
    
    // Extracted properties with fallbacks
    const hover = this.extractCssProperty(css, 'ui-state-active', 'background-color');
    styles.hoverBg = hover ? hover : defaultStyles.hoverBg;

    const activeText = this.extractCssProperty(css, 'ui-state-active', 'color');
    styles.activeText = (activeText && activeText !== 'inherit') ? activeText : defaultStyles.activeText;

    const height = this.extractCssProperty(css, 'controlLiner', 'line-height');
    styles.controlHeight = height ? height : defaultStyles.controlHeight;

    const radius = this.extractCssProperty(css, 'controlLiner', 'border-radius') || this.extractCssProperty(css, 'ui-autocomplete-input', 'border-radius');
    styles.borderRadius = radius ? radius : defaultStyles.borderRadius;

    const stroke = this.extractCssProperty(css, 'svg.styleableColor path', 'stroke') || this.extractCssProperty(css, 'svg.styleableColor rect', 'stroke');
    const border = this.extractCssProperty(css, 'controlLiner', 'border-color') || this.extractCssProperty(css, 'controlLiner', 'border');
    styles.borderColor = stroke ? stroke : (border ? border : defaultStyles.borderColor);

    const inputBg = this.extractCssProperty(css, 'controlLiner', 'background-color');
    styles.inputBg = inputBg ? inputBg : defaultStyles.inputBg;

    const chip = this.extractCssProperty(css, 'ui-state-active', 'background-color');
    styles.chipBg = chip ? chip : defaultStyles.hoverBg;

    const disabled = this.extractCssProperty(css, 'ui-state-disabled', 'color');
    styles.disabledColor = disabled ? disabled : defaultStyles.disabledColor;

  
    styles.controlMaxWidth = defaultStyles.controlMaxWidth; 

    
    if (typeof css === 'string') {
      const inputMatch = css.match(/input\[type=text\][^}]*\{([^}]+)\}/i);
      const mwMatch = inputMatch?.[1]?.match(/max-width:\s*([^;]+)/i);
      if (mwMatch?.[1]) {
        styles.controlMaxWidth = mwMatch[1].trim();
      }
    }    
    return styles;
  }

 
  private extractCssProperty(cssString: string, targetClass: string, property: string): string | null {
  try {

    // Match selectors containing the target class
    const blockRegex = new RegExp(
      `(?:[^}{]*\\.${targetClass}(?![\\w-])[^}{]*)\\{([^}]+)\\}`,
      'gi'
    );

    let blockMatch;

    while ((blockMatch = blockRegex.exec(cssString)) !== null) {
      const rules = blockMatch[1];
      // Exact property match only
      // prevents "color" matching "background-color"
      const propRegex = new RegExp(
        `(?<![-\\w])${property}\\s*:\\s*([^;]+)`,
        'i'
      );

      const propMatch = rules.match(propRegex);

      if (propMatch && propMatch[1]) {
        return propMatch[1].trim();
      }
    }
  } catch (e) {
    console.warn('CSS Extraction failed for', targetClass, property, e);
  }
  return null;
}
  private createSettingsLoadObservable(): Observable<TgSettingsResponse> {
    const params = this.getPartnerAndSiteFromUrl();
    if (params.partnerId === 0 || params.siteId === 0) {
      console.log("[TgSettingsService] Missing partnerId or siteId in URL, skipping settings load");
      return of({ tgSettings: {}, headerInfo: [], footerInfo: [] });
    }

    const url = `${this.apiBase}/AngularTgsettingLoad?partnerId=${params.partnerId}&siteId=${params.siteId}`;

    return this.http.get<TgSettingsResponse>(url).pipe(
      tap(settings => {
        this.settingsSubject.next(settings);
      }),
      catchError(err => {
        console.error('[TgSettingsService] Failed to load settings', err);
        this.settingsSubject.next({ tgSettings: {}, headerInfo: [], footerInfo: [] });
        return of({ tgSettings: {}, headerInfo: [], footerInfo: [] });
      })
    );
  }
  getSearchResJobFieldsToDisplay(): JobFieldsToDisplay | undefined {
    return this.settingsSubject.value?.searchresJobFieldsToDisplay;
  }

  getSettings(): Observable<TgSettingsResponse> {
    return this.settingsLoad$;
  }

  getCurrentSettings(): TgSettingsResponse | null {
    return this.settingsSubject.value;
  }
  getBrandingConfig$(): Observable<BrandingSettingsResponse | undefined> {
    return this.settings$.pipe(
      map(settings => settings?.BrandingConfiguration)
    );
    //return this.settingsSubject.value?.BrandingConfiguration;
  }
  getLocaleId(): number {
    const settings = this.getCurrentSettings()?.tgSettings;
    const id = Number(settings?.DefLocaleId);
    return isNaN(id) ? 1033 : id;
  }
  getLocale(): string {

    return LCID_TO_LANG[this.getLocaleId()] ?? 'en-gb';

  }
  getdateFormat(): number {
    const settings = this.getCurrentSettings()?.tgSettings;
    const date = Number(settings?.DateFormat);
    return isNaN(date) ? 3 : date;

  }

  private getPartnerAndSiteFromUrl(): { partnerId: number; siteId: number } {
    const params = new URLSearchParams(window.location.search);
    return {
      partnerId: Number(params.get('partnerid')) || 0,
      siteId: Number(params.get('siteid')) || 0
    };
  }
}
